import * as React from "react";

import { Skeleton } from "@/components/ui/states";
import type { MeBranding } from "@/api/me";
import { brandingAssetUrl } from "@/lib/brandingAssets";
import { cn } from "@/lib/cn";

/**
 * Brandmark (MV2-844, ds/WhiteLabelSwap) — the tenant mark in the rail.
 *
 * The states most tenants are actually in, designed rather than defaulted:
 *
 *   switching   /me is mid-fetch after actingAs changed → a neutral skeleton
 *               holds the box. Never the previous tenant's logo (R2), never
 *               an empty box.
 *   resolving   branding is known but the image has not decoded yet → the
 *               tenant's app_name set in type at 13/600 holds the space.
 *   no logo     the platform's mark, inherited. MV2-969 reversed MV2-849
 *               here: re:bot branding is the default every tenant wears
 *               until they upload their own, rather than being stripped
 *               so that "a tenant never wears re:bot's wordmark". Only an
 *               upload overrides it.
 *   light only  in dark theme (only 2 of 9 have a dark logo) → the light
 *               logo on a white chip with 3px padding. Never inverted —
 *               inverting a logo is a trademark problem, not a styling one.
 *   platform    no tenant branding at all → the same re:bot marks, which
 *               is now simply the "no logo" case with no name set either.
 *
 * Light and dark variants are both rendered and swapped by CSS so a theme
 * toggle costs no React render.
 */
export interface BrandmarkProps {
  branding: MeBranding | null | undefined;
  /** True while /me is in flight — the switching state. */
  loading?: boolean;
  collapsed?: boolean;
  /**
   * MV2-845 rail header. Collapsed: a 24px mark (mini logo, or a
   * brand-primary square with the initials). With `withName` (expanded rail,
   * owner header): the FULL-LENGTH logo, capped at 28px tall — Kranthi,
   * 8 Sep — falling back to the mark + app_name at 13/600 for the six of
   * nine tenants without one. Platform default: the RenewableBot wordmark.
   */
  rail?: boolean;
  withName?: boolean;
  className?: string;
}

const boxCls = "mx-auto max-h-9 object-contain";

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

export function Brandmark({
  branding,
  loading = false,
  collapsed = false,
  rail = false,
  withName = false,
  className,
}: BrandmarkProps): React.ReactElement {
  if (rail) {
    return (
      <RailMark
        branding={branding}
        loading={loading}
        withName={withName}
        className={className}
      />
    );
  }
  // Light/dark × expanded/collapsed resolution. The dark side falls through
  // to the light asset — and remembers that it did, so it can go on a chip.
  const lightKey = collapsed
    ? (branding?.mini_logo_s3_key ??
      branding?.mini_logo_dark_s3_key ??
      branding?.logo_s3_key ??
      branding?.logo_dark_s3_key ??
      null)
    : (branding?.logo_s3_key ?? branding?.logo_dark_s3_key ?? null);
  const darkOwnKey = collapsed
    ? (branding?.mini_logo_dark_s3_key ?? branding?.logo_dark_s3_key ?? null)
    : (branding?.logo_dark_s3_key ?? null);
  const darkKey =
    darkOwnKey ??
    (collapsed
      ? (branding?.mini_logo_s3_key ?? branding?.logo_s3_key ?? null)
      : (branding?.logo_s3_key ?? null));
  const darkIsLightOnChip = darkOwnKey == null && darkKey != null;

  const light = brandingAssetUrl(lightKey);
  const dark = brandingAssetUrl(darkKey);
  const srcs = React.useMemo(
    () => [...new Set([light, dark].filter((s): s is string => !!s))],
    [light, dark],
  );

  // The neutral mark holds until every variant has decoded.
  const [loaded, setLoaded] = React.useState<ReadonlySet<string>>(new Set());
  const resolving = srcs.some((s) => !loaded.has(s));
  const onLoad = (src: string) =>
    setLoaded((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));

  const name = branding?.app_name?.trim() || "";
  const wordmark = (
    <span
      className={cn(
        "truncate text-[13px] font-semibold tracking-[-.01em] text-gray-900",
        collapsed && "tabular",
      )}
      title={collapsed ? name : undefined}
    >
      {collapsed ? initials(name) : name}
    </span>
  );

  if (loading && !branding) {
    return (
      <span
        className={cn("flex items-center justify-center", className)}
        aria-busy="true"
        aria-label="Loading"
        data-testid="brandmark-switching"
      >
        <Skeleton
          className={collapsed ? "h-9 w-9 rounded-[8px]" : "h-4 w-28"}
        />
      </span>
    );
  }

  if (!branding) {
    return collapsed ? (
      <span className={className}>
        <img
          src="/brand/SVG/Light_rounded_icon.svg"
          alt="RenewableBot"
          className={cn(boxCls, "max-w-9 block dark:hidden")}
        />
        <img
          src="/brand/SVG/Dark_rounded_icon.svg"
          alt="RenewableBot"
          className={cn(boxCls, "max-w-9 hidden dark:block")}
        />
      </span>
    ) : (
      <span className={className}>
        <img
          src="/brand/SVG/Light_Primary.svg"
          alt="RenewableBot"
          className={cn(boxCls, "max-w-full block dark:hidden")}
        />
        <img
          src="/brand/SVG/Dark_Primary.svg"
          alt="RenewableBot"
          className={cn(boxCls, "max-w-full hidden dark:block")}
        />
      </span>
    );
  }

  if (srcs.length === 0) {
    return (
      <span
        className={cn("flex items-center justify-center", className)}
        data-testid="brandmark-wordmark"
      >
        {wordmark}
      </span>
    );
  }

  return (
    <span className={cn("flex items-center justify-center", className)}>
      {resolving && (
        <span data-testid="brandmark-resolving" className="flex items-center">
          {wordmark}
        </span>
      )}
      {light && (
        <img
          key={`light-${light}`}
          src={light}
          alt={name || "Tenant"}
          onLoad={() => onLoad(light)}
          className={cn(
            boxCls,
            collapsed ? "max-w-9" : "max-w-full",
            "dark:hidden",
            resolving ? "hidden" : "block",
          )}
        />
      )}
      {dark && (
        <span
          data-testid={darkIsLightOnChip ? "brandmark-chip" : undefined}
          className={cn(
            // Hidden while resolving in both themes; CSS shows it in dark once decoded.
            resolving ? "hidden" : "hidden dark:block",
            darkIsLightOnChip && "rounded-[5px] bg-white p-[3px]",
          )}
        >
          <img
            key={`dark-${dark}`}
            src={dark}
            alt={name || "Tenant"}
            onLoad={() => onLoad(dark)}
            className={cn(
              boxCls,
              collapsed ? "max-w-9" : "max-w-full",
              darkIsLightOnChip && "max-h-[30px]",
            )}
          />
        </span>
      )}
    </span>
  );
}

function RailMark({
  branding,
  loading,
  withName,
  className,
}: {
  branding: MeBranding | null | undefined;
  loading: boolean;
  withName: boolean;
  className?: string;
}): React.ReactElement {
  const name = branding?.app_name?.trim() || "";
  const lightKey =
    branding?.mini_logo_s3_key ??
    branding?.mini_logo_dark_s3_key ??
    branding?.logo_s3_key ??
    branding?.logo_dark_s3_key ??
    null;
  const darkOwnKey =
    branding?.mini_logo_dark_s3_key ?? branding?.logo_dark_s3_key ?? null;
  const darkKey =
    darkOwnKey ?? branding?.mini_logo_s3_key ?? branding?.logo_s3_key ?? null;
  const onChip = darkOwnKey == null && darkKey != null;
  const light = brandingAssetUrl(lightKey);
  const dark = brandingAssetUrl(darkKey);
  const box =
    "flex h-6 w-6 flex-none items-center justify-center overflow-hidden rounded-[7px]";

  if (loading && !branding) {
    return (
      <span
        className={cn("flex items-center gap-[9px]", className)}
        aria-busy="true"
        data-testid="brandmark-switching"
      >
        <Skeleton className="h-6 w-6 rounded-[7px]" />
        {withName && <Skeleton className="h-3 w-24" />}
      </span>
    );
  }
  // Expanded: the full wordmark when the tenant has one.
  const fullLight = brandingAssetUrl(
    branding?.logo_s3_key ?? branding?.logo_dark_s3_key ?? null,
  );
  const fullDarkOwn = brandingAssetUrl(branding?.logo_dark_s3_key ?? null);
  const fullDark = fullDarkOwn ?? fullLight;
  // The wordmark scales to the space it is given: as wide as the container
  // allows, never taller than 32px, aspect ratio kept — a wide wordmark fills
  // the rail, a squarer logo sits centred at full height (Kranthi, 8 Sep).
  const WORDMARK = "block h-auto max-h-8 w-auto max-w-full object-contain";
  if (withName && !branding && !loading) {
    return (
      <span className={cn("flex h-8 min-w-0 items-center", className)}>
        <img
          src="/brand/SVG/Light_Primary.svg"
          alt="RenewableBot"
          className={cn(WORDMARK, "dark:hidden")}
        />
        <img
          src="/brand/SVG/Dark_Primary.svg"
          alt="RenewableBot"
          className={cn(WORDMARK, "hidden dark:block")}
        />
      </span>
    );
  }
  if (withName && branding && fullLight) {
    return (
      <span
        className={cn("flex h-8 min-w-0 items-center", className)}
        data-testid="brandmark-wordmark-logo"
      >
        <img
          src={fullLight}
          alt={name || "Tenant"}
          className={cn(WORDMARK, "dark:hidden")}
        />
        <span
          className={cn(
            "hidden h-full min-w-0 max-w-full items-center justify-center dark:flex",
            !fullDarkOwn && "rounded-[5px] bg-white px-1 py-0.5",
          )}
          data-testid={!fullDarkOwn ? "brandmark-chip" : undefined}
        >
          <img
            src={fullDark ?? undefined}
            alt={name || "Tenant"}
            className={cn(WORDMARK, !fullDarkOwn && "max-h-7")}
          />
        </span>
      </span>
    );
  }

  let mark: React.ReactNode;
  if (!branding) {
    mark = (
      <span className={box}>
        <img
          src="/brand/SVG/Light_rounded_icon.svg"
          alt="RenewableBot"
          className="h-6 w-6 dark:hidden"
        />
        <img
          src="/brand/SVG/Dark_rounded_icon.svg"
          alt="RenewableBot"
          className="hidden h-6 w-6 dark:block"
        />
      </span>
    );
  } else if (!light && !dark) {
    mark = (
      <span
        data-testid="brandmark-square"
        className={cn(
          box,
          "bg-[color:var(--brand-primary)] text-[11px] font-bold tracking-[-.03em] text-[color:var(--brand-primary-fg)]",
        )}
        aria-hidden
      >
        {initials(name)}
      </span>
    );
  } else {
    mark = (
      <>
        {light && (
          <span className={cn(box, "dark:hidden")}>
            <img
              src={light}
              alt={name || "Tenant"}
              className="max-h-6 max-w-6 object-contain"
            />
          </span>
        )}
        {dark && (
          <span
            className={cn(
              box,
              "hidden dark:flex",
              onChip && "bg-white p-[2px]",
            )}
            data-testid={onChip ? "brandmark-chip" : undefined}
          >
            <img
              src={dark}
              alt={name || "Tenant"}
              className="max-h-full max-w-full object-contain"
            />
          </span>
        )}
      </>
    );
  }
  return (
    <span className={cn("flex min-w-0 items-center gap-[9px]", className)}>
      {mark}
      {withName && (
        <span className="truncate text-[13px] font-semibold text-gray-900">
          {branding ? name : "RenewableBot"}
        </span>
      )}
    </span>
  );
}
